import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

/** Omitted from data pool detail dialog (noisy / internal server keys). */
const HIDDEN_DATA_POOL_DETAIL_KEYS = new Set(['pool:mailbox:messages']);

/** Shared grid for data pool list rows and detail header (name / description / type). */
const DATA_POOL_GRID_CLASS =
    'grid grid-cols-[minmax(96px,1.1fr)_minmax(120px,2fr)_minmax(72px,0.75fr)]';

function safeJsonStringify(value: unknown, space = 2): string {
    try {
        return JSON.stringify(value, null, space);
    } catch {
        return String(value);
    }
}

function normDataPoolStr(value: unknown): string {
    return String(value ?? '')
        .trim()
        .toLowerCase();
}

function shouldHideDataPoolDisplayValue(value: unknown): boolean {
    return HIDDEN_DATA_POOL_DETAIL_KEYS.has(String(value ?? ''));
}

/**
 * System / connector / inbound-e-mail schema: hide Quelle, Knoten, Zeit, and Schema lines in the detail dialog.
 * Matches e.g. Quelle system, Knoten connector, Schema object — Inbound e-mail that started this process
 */
const shouldHideInboundEmailPoolSummary = (item: any): boolean => {
    const src = normDataPoolStr(item?.source);
    const node = normDataPoolStr(item?.node);
    const schema = item?.schema;
    if (schema == null || typeof schema !== 'object') return false;
    const type = normDataPoolStr((schema as any).type);
    const desc = normDataPoolStr((schema as any).description);
    const inboundPhrase =
        desc.includes('inbound e-mail that started this process') ||
        desc.includes('inbound email that started this process');
    return src === 'system' && node === 'connector' && type === 'object' && inboundPhrase;
};

/**
 * Resolved type used for rendering stored values (metadata.kind, then schema.type).
 * Does not map missing types to «–»; used for payload layout, not the type column.
 */
function resolveDataPoolPayloadType(item: any): string {
    const m = item?.metadata;
    if (m && typeof m === 'object' && (m as any).kind != null && String((m as any).kind).trim() !== '') {
        const kind = String((m as any).kind);
        if (!shouldHideDataPoolDisplayValue(kind)) return kind;
    }
    const s = item?.schema;
    if (s && typeof s === 'object' && (s as any).type != null && String((s as any).type).trim() !== '') {
        const st = String((s as any).type);
        if (!shouldHideDataPoolDisplayValue(st)) return st;
    }
    return '';
}

function getDataPoolItemType(item: any): string {
    if (shouldHideInboundEmailPoolSummary(item)) return '–';
    const resolved = resolveDataPoolPayloadType(item);
    return resolved !== '' ? resolved : '–';
}

function formatDataPoolDisplayedValue(data: unknown, valueTypeNorm: string): string {
    if (data === undefined || data === null) return '–';
    if (valueTypeNorm === 'array') {
        return safeJsonStringify(data);
    }
    if (valueTypeNorm === 'object') {
        return safeJsonStringify(data ?? {});
    }
    if (typeof data === 'object') {
        return safeJsonStringify(data);
    }
    return String(data);
}

/** Payload block; clicking (or activating via keyboard) copies the value to the clipboard. */
function DataPoolPayloadSection({ text }: { text: string }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const copyValue = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
        } catch (err) {
            console.error('Failed to copy data pool value:', err);
        }
    };

    return (
        <div className="mt-3 border-t pt-3">
            <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-semibold">
                    {t('processPanel.dataPoolPayload')}
                </span>
                <span className={cn('text-xs', copied ? 'text-success' : 'text-muted-foreground')}>
                    {copied ? t('global.copied') : t('ui.copyToClipboard')}
                </span>
            </div>
            <pre
                role="button"
                tabIndex={0}
                aria-label={t('ui.copyToClipboard')}
                title={t('ui.copyToClipboard')}
                onClick={copyValue}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        copyValue();
                    }
                }}
                className="m-0 max-h-[min(55vh,720px)] cursor-pointer overflow-auto whitespace-pre-wrap break-words rounded-md border bg-muted p-3 font-mono text-xs"
            >
                {text}
            </pre>
        </div>
    );
}

function DataPoolItemDetailContent({ item }: { item: any }) {
    const { t } = useTranslation();
    const name =
        String(item?.name ?? '')
            .trim() || t('processPanel.dataPoolItemDialogTitle');
    const desc = item?.description != null ? String(item.description).trim() : '';
    const typeLabel = getDataPoolItemType(item);
    const valueType = resolveDataPoolPayloadType(item);
    const valueTypeNorm = normDataPoolStr(valueType);
    const isObjectType = valueTypeNorm === 'object';
    const data = item?.data;
    const primitivePayloadTypes = new Set(['string', 'number', 'integer', 'boolean', 'array']);
    const showPrimitivePayload = !isObjectType && primitivePayloadTypes.has(valueTypeNorm);
    /** Server may omit schema/kind while still returning a string in `data`. */
    const showStringDataFallback =
        !isObjectType && !showPrimitivePayload && valueTypeNorm === '' && typeof data === 'string';

    return (
        <div>
            <div
                className={cn(
                    DATA_POOL_GRID_CLASS,
                    'items-start gap-4',
                    isObjectType || showPrimitivePayload || showStringDataFallback ? 'mb-3' : 'mb-0',
                )}
            >
                <div className="min-w-0">
                    <div className="mb-1 text-xs font-semibold text-muted-foreground">{t('processPanel.dataPoolListName')}</div>
                    <div className="break-words text-base font-semibold">{name}</div>
                </div>
                <div className="min-w-0">
                    <div className="mb-1 text-xs font-semibold text-muted-foreground">{t('processPanel.dataPoolListDescription')}</div>
                    <div className="break-words text-sm leading-relaxed">{desc || '–'}</div>
                </div>
                <div className="min-w-0">
                    <div className="mb-1 text-xs font-semibold text-muted-foreground">{t('processPanel.dataPoolListType')}</div>
                    <div className="break-words text-sm">{typeLabel}</div>
                </div>
            </div>
            {isObjectType && <DataPoolPayloadSection text={safeJsonStringify(data ?? {})} />}
            {(showPrimitivePayload || showStringDataFallback) && (
                <DataPoolPayloadSection
                    text={formatDataPoolDisplayedValue(
                        data,
                        showStringDataFallback ? 'string' : valueTypeNorm
                    )}
                />
            )}
        </div>
    );
}

export interface ProcessInstanceDataPoolProps {
    items: any[] | null | undefined;
    itemCount?: number;
    /** When true, only the completion notice is shown; pool entries are not listed. */
    processCompleted?: boolean;
}

export const ProcessInstanceDataPool: React.FC<ProcessInstanceDataPoolProps> = ({
    items,
    itemCount,
    processCompleted = false,
}) => {
    const { t } = useTranslation();
    const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
    const count = itemCount ?? list.length;
    const [detail, setDetail] = useState<{ item: any; index: number } | null>(null);

    if (processCompleted) {
        return (
            <div className="rounded-md border bg-muted px-4 py-3 text-sm">
                {t('processPanel.dataPoolProcessCompleted')}
            </div>
        );
    }

    if (list.length === 0) {
        return (
            <div className="text-sm text-muted-foreground">
                {t('processPanel.processDataPoolEmpty')}
            </div>
        );
    }

    const dialogTitle =
        detail != null
            ? String(detail.item?.name ?? '').trim() || `${t('processPanel.dataPoolItemDialogTitle')} ${detail.index + 1}`
            : '';

    return (
        <div>
            <div className="mb-5 text-sm">
                {t('processPanel.processDataPoolCount', { count })}
            </div>
            <div className="overflow-hidden rounded-lg border bg-card">
                <div
                    className={cn(
                        DATA_POOL_GRID_CLASS,
                        'gap-3 border-b bg-muted px-4 py-2.5 text-xs font-semibold text-muted-foreground',
                    )}
                >
                    <span>{t('processPanel.dataPoolListName')}</span>
                    <span>{t('processPanel.dataPoolListDescription')}</span>
                    <span>{t('processPanel.dataPoolListType')}</span>
                </div>
                {list.map((item, idx) => {
                    const name = String(item?.name ?? '').trim() || `${t('processPanel.dataPoolItemDialogTitle')} ${idx + 1}`;
                    const descRaw = item?.description != null ? String(item.description).trim() : '';
                    const typeStr = getDataPoolItemType(item);
                    return (
                        <div
                            key={item?.id ?? idx}
                            role="button"
                            tabIndex={0}
                            aria-label={t('processPanel.dataPoolOpenEntry')}
                            onClick={() => setDetail({ item, index: idx })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setDetail({ item, index: idx });
                                }
                            }}
                            className={cn(
                                DATA_POOL_GRID_CLASS,
                                'cursor-pointer items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
                                idx < list.length - 1 && 'border-b',
                            )}
                        >
                            <span className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold" title={name}>
                                {name}
                            </span>
                            <span
                                className={cn(
                                    'overflow-hidden text-ellipsis whitespace-nowrap',
                                    !descRaw && 'text-muted-foreground',
                                )}
                                title={descRaw || undefined}
                            >
                                {descRaw || '–'}
                            </span>
                            <span
                                className="max-w-full justify-self-start overflow-hidden text-ellipsis whitespace-nowrap rounded bg-muted px-2 py-0.5 text-xs"
                                title={typeStr !== '–' ? typeStr : undefined}
                            >
                                {typeStr}
                            </span>
                        </div>
                    );
                })}
            </div>

            <Dialog
                open={detail !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setDetail(null);
                    }
                }}
            >
                <DialogContent className="w-[min(1280px,94vw)] sm:max-w-[1280px]">
                    <DialogHeader>
                        <DialogTitle>{dialogTitle}</DialogTitle>
                    </DialogHeader>
                    {detail && (
                        <div className="max-h-[70vh] overflow-auto pr-1">
                            <DataPoolItemDetailContent item={detail.item} />
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDetail(null)}>{t('global.close')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
